import {
  availableVFATestConfigurationOptions,
  isBinocularVFATestType,
  isStandardVFATestType,
  selectableVFABehavioursFor,
} from "@constants/testConstants/testSpecificConstants";
import { WebSocketMessage } from "model/WebSocketMessage";
import { UnityVFATestStringResponse } from "types/api/payloadTypes";
import {
  VFATestSnapshot,
  VFACollectedData,
  FPFNInfo,
  TestConfiguration,
  TestCategory,
  EyeConfiguration,
  WaitDuration,
  TestLanguage,
  VoiceAssistantConfig,
  VFATestCustomConfiguration,
  AvailableOption,
  AvailablePreTestConfigurationOptions,
  TestType,
  VFABehaviour,
  OptionalVFATests,
  GoldmannSize,
  AvailableVFASpecificConfigurationOptions,
} from "types/tests";
import { cloneDeep } from "lodash";
import { countsFixationLossesOnMovingFixation, isSuprathresholdBehaviour } from "@utils/TestUtils";
import {
  MovingFixationCatchTrialAvailability,
  presentsMovingFixationCatchTrials,
} from "@utils/VFA/movingFixationCatchTrials";

function annotateVFABehaviourChoiceAvailability(
  vfaBehaviourOptions: AvailableOption<VFABehaviour | OptionalVFATests>,
  testType: TestType | undefined,
): AvailableOption<VFABehaviour | OptionalVFATests> {
  const selectableBehaviours = selectableVFABehavioursFor(testType);

  return {
    ...vfaBehaviourOptions,
    choices: vfaBehaviourOptions.choices?.map((choice) => ({
      ...choice,
      disabled: !selectableBehaviours.includes(choice.value as VFABehaviour),
    })),
  };
}

export function GetUpdatedAvailableVFAConfigsAfterConfigUpdate(
  requestedUpdate: Partial<TestConfiguration>,
  baseOptions?: AvailablePreTestConfigurationOptions | null,
  catchTrialAvailability: MovingFixationCatchTrialAvailability = { catchTrialsSwitchedOn: false },
): AvailablePreTestConfigurationOptions {
  let availableVFAConfig: AvailablePreTestConfigurationOptions = cloneDeep(
    baseOptions ?? availableVFATestConfigurationOptions,
  );

  const testType: TestType | undefined = requestedUpdate.testType;
  const testStyle: VFABehaviour | undefined = (
    requestedUpdate.extraData as VFATestCustomConfiguration
  )?.testBehaviour;

  const vfaSpecificOptions =
    availableVFAConfig.testSpecificConfigurationOptions as AvailableVFASpecificConfigurationOptions;

  if (vfaSpecificOptions?.vfaBehaviourOptions) {
    availableVFAConfig = {
      ...availableVFAConfig,
      testSpecificConfigurationOptions: {
        ...vfaSpecificOptions,
        vfaBehaviourOptions: annotateVFABehaviourChoiceAvailability(
          vfaSpecificOptions.vfaBehaviourOptions,
          testType,
        ),
      },
    };
  }

  if (!isStandardVFATestType(testType)) {
    const { toggleFovealThresholdTesting, ...remainingOptions } =
      availableVFAConfig.testSpecificConfigurationOptions as AvailableVFASpecificConfigurationOptions;

    availableVFAConfig = {
      ...availableVFAConfig,
      testSpecificConfigurationOptions: remainingOptions,
    };
  }

  if (isBinocularVFATestType(testType)) {
    const { testSpeedOptions, ...remainingOptions } = availableVFAConfig;
    const { eyeTypes, toggleTwoScreenFixationOption, ...remainingEyeConfigOptions } =
      availableVFAConfig.eyeConfigurationOptions;
    const { stimuliSizeOptions, ...remainingTestSpecificOptions } =
      availableVFAConfig.testSpecificConfigurationOptions as AvailableVFASpecificConfigurationOptions;

    availableVFAConfig = {
      ...remainingOptions,
      eyeConfigurationOptions: remainingEyeConfigOptions,
      testSpecificConfigurationOptions: remainingTestSpecificOptions,
    };
  }

  if (testStyle === VFABehaviour.SWAP_TOP) {
    const { stimuliSizeOptions, toggleFovealThresholdTesting, ...remainingOptions } =
      availableVFAConfig.testSpecificConfigurationOptions as AvailableVFASpecificConfigurationOptions;

    availableVFAConfig = {
      ...availableVFAConfig,
      testSpecificConfigurationOptions: remainingOptions,
    };
  }

  const isOculeraInteractive =
    testStyle === VFABehaviour.SITA_LIKE || testStyle === VFABehaviour.FULL_THRESH_4;
  const usesBlindSpotCatchTrials = presentsMovingFixationCatchTrials(
    testType,
    testStyle,
    catchTrialAvailability,
  );
  if (!isOculeraInteractive && !usesBlindSpotCatchTrials) {
    const { toggleBlindSpotTesting, toggleBlindSpotMonitoring, ...remainingOptions } =
      availableVFAConfig.testSpecificConfigurationOptions as AvailableVFASpecificConfigurationOptions;
    availableVFAConfig = {
      ...availableVFAConfig,
      testSpecificConfigurationOptions: remainingOptions,
    };
  }
  return availableVFAConfig;
}

export function ExtractCollectedVFADataFromTestString(
  testString: UnityVFATestStringResponse,
): VFACollectedData {
  const pointValues: number[] = testString.points;
  const seenFL: number = 0;
  const warning: number = 0;

  return {
    pointValues,
    seenFL,
    warning,
    fixation: "",
  };
}

export function ConvertVFASnapshotToCollectedData(
  snapshot: VFATestSnapshot,
  currentCollected?: VFACollectedData,
): VFACollectedData | undefined {
  const currentColoredPoints = currentCollected?.pointValues;
  const currentSeenFL = currentCollected?.seenFL ?? 0;
  const currentWarning = currentCollected?.warning ?? 0;
  const nextFovealThreshold = snapshot.fovealThreshold ?? currentCollected?.fovealThreshold;
  const nextBlindSpotSeen = snapshot.blindSpotSeen ?? currentCollected?.blindSpotSeen;
  const nextBlindSpotTotal = snapshot.blindSpotTotal ?? currentCollected?.blindSpotTotal;

  if (!currentColoredPoints) {
    console.warn("Received VFATestSnapshot but currentCollected.pointValues is undefined.");
    return;
  }

  if (snapshot.index === undefined || snapshot.value === undefined) {
    return {
      ...currentCollected,
      fpFnInfo: snapshot.fpFnInfo ?? currentCollected?.fpFnInfo,
      fovealThreshold: nextFovealThreshold,
      blindSpotSeen: nextBlindSpotSeen,
      blindSpotTotal: nextBlindSpotTotal,
    };
  }

  const index = snapshot.index;
  const val = snapshot.value;
  const newColors = [...currentColoredPoints];
  if (index < 0 || index >= newColors.length) {
    console.warn(
      `Index ${index} is out of bounds for coloredPoints array of length ${newColors.length}.`,
    );
    return;
  }

  newColors[index] = val;

  const blindSpotSeen = snapshot.blindSpotSeen ?? 0;
  const blindSpotTotal = snapshot.blindSpotTotal ?? 0;
  const ratio = blindSpotTotal > 0 ? blindSpotSeen / blindSpotTotal : 0;

  if (ratio > 0.3 && blindSpotSeen > currentSeenFL) {
    return {
      pointValues: newColors,
      seenFL: blindSpotSeen,
      warning: currentWarning + 1,
      fixation: snapshot.fixation || "",
      fpFnInfo: snapshot.fpFnInfo ?? currentCollected?.fpFnInfo,
      fovealThreshold: nextFovealThreshold,
      blindSpotSeen: nextBlindSpotSeen,
      blindSpotTotal: nextBlindSpotTotal,
    };
  }

  return {
    ...currentCollected,
    pointValues: newColors,
    fpFnInfo: snapshot.fpFnInfo ?? currentCollected?.fpFnInfo,
    fovealThreshold: nextFovealThreshold,
    blindSpotSeen: nextBlindSpotSeen,
    blindSpotTotal: nextBlindSpotTotal,
  };
}

export function isVFASnapshot(msg: WebSocketMessage): boolean {
  return (
    "index" in msg ||
    "value" in msg ||
    "fpFnInfo" in msg ||
    "fnFpInfo" in msg ||
    "blindSpotSeen" in msg ||
    "blindSpotTotal" in msg ||
    "fovealThreshold" in msg
  );
}

export function ExtractVFASnapshotData(msg: WebSocketMessage): VFATestSnapshot | null {
  if (isVFASnapshot(msg)) {
    {
      const blindSpotSeen = msg["blindSpotSeen"] as number;
      const blindSpotTotal = msg["blindSpotTotal"] as number;
      const fixation =
        blindSpotSeen !== undefined && blindSpotTotal !== undefined
          ? blindSpotSeen + "/" + blindSpotTotal
          : undefined;
      const vfaSnapshot: VFATestSnapshot = {
        index: msg["index"] as number,
        value: msg["value"] as number,
        blindSpotSeen: msg["blindSpotSeen"] as number,
        blindSpotTotal: msg["blindSpotTotal"] as number,
        fpFnInfo: (msg["fpFnInfo"] ?? msg["fnFpInfo"]) as FPFNInfo,
        fixation: fixation,
        fovealThreshold: msg["fovealThreshold"] as number,
      };
      return vfaSnapshot;
    }
  }
  return null;
}

export function ExtractVFAConfigurationFromTestString(
  testString: UnityVFATestStringResponse,
  existingConfiguration: TestConfiguration | null = null,
): TestConfiguration {
  // config may be a double-encoded JSON string from the headset
  let parsedConfig = testString.config;
  if (typeof parsedConfig === "string") {
    try {
      parsedConfig = JSON.parse(parsedConfig);
    } catch {
      parsedConfig = null;
    }
  }

  // Headsets in the field don't all echo the full config in
  // CURRENT_TEST_DETAILS. When this session configured the same test, fall
  // back to its configuration for fields the payload omits, so a resumed
  // progress page doesn't lose auto-pause, binocular fixation, etc.
  const existing =
    existingConfiguration?.name === TestCategory.VisualField &&
    existingConfiguration.testType === testString.testType
      ? existingConfiguration
      : null;
  const existingEye = existing?.eyeConfiguration ?? null;
  const existingExtra = (existing?.extraData as VFATestCustomConfiguration | undefined) ?? null;

  const eyeConfiguration: EyeConfiguration = {
    eyeType: testString.eye,
    isCalibrationNeeded: false,
    isBackToBackEyeTest: false,
    autoPauseForGaze: parsedConfig?.autoPauseForGaze ?? existingEye?.autoPauseForGaze,
    isTwoScreenFixationEnabled:
      parsedConfig?.twoScreenFixation ?? existingEye?.isTwoScreenFixationEnabled,
  };
  let testSpeed: WaitDuration | null = null;
  switch (parsedConfig?.defaultMustWait) {
    case 400:
      testSpeed = WaitDuration.Standard;
      break;
    case 800:
      testSpeed = WaitDuration.Slow;
      break;
    case 1000:
      testSpeed = WaitDuration.Slower;
      break;
    default:
      testSpeed = existing?.testSpeed ?? null;
  }

  const voiceAssistantLanguage: TestLanguage | null = parsedConfig?.voiceAssistantLanguage
    ? (parsedConfig.voiceAssistantLanguage as TestLanguage)
    : null;
  const voiceAssistantConfig: VoiceAssistantConfig = voiceAssistantLanguage
    ? { assistantLanguage: voiceAssistantLanguage, isVoiceAssistantEnabled: true }
    : (existing?.voiceAssistantConfig ?? {
        assistantLanguage: TestLanguage.NOT_SPECIFIED,
        isVoiceAssistantEnabled: false,
      });

  const extraData: VFATestCustomConfiguration = {
    blindSpotTesting: parsedConfig?.blindSpotTesting ?? existingExtra?.blindSpotTesting ?? false,
    testBehaviour: testString.testBehaviour,
    blindSpotMonitoringEnabled:
      parsedConfig?.blindSpotMonitoringEnabled ??
      existingExtra?.blindSpotMonitoringEnabled ??
      false,
    fovealThresholdTestingEnabled:
      parsedConfig?.fovealThresholdTestingEnabled ??
      existingExtra?.fovealThresholdTestingEnabled ??
      false,
    enablePausingTheTestWithButton:
      parsedConfig?.enablePausingTheTestWithButton ??
      existingExtra?.enablePausingTheTestWithButton ??
      false,
    showTutorial: false,
    fixationSize: parsedConfig?.fixationSize ?? existingExtra?.fixationSize,
    stimuliSize: parsedConfig?.stimuliSize ?? existingExtra?.stimuliSize,
  };
  const config: TestConfiguration = {
    name: TestCategory.VisualField,
    testType: testString.testType,
    eyeConfiguration: eyeConfiguration,
    isPressToStartEnabled: true,
    testLanguage: existing?.testLanguage ?? null,
    testSpeed: testSpeed,
    virtualAssistanceConfig:
      parsedConfig?.virtualAssistantConfig || existing?.virtualAssistanceConfig || null,
    voiceAssistantConfig: voiceAssistantConfig,
    extraData: extraData,
  };
  return config;
}

function isMovingFixationSuprathreshold(
  configuration: Partial<TestConfiguration> | null | undefined,
) {
  const testType = configuration?.testType;
  const testBehaviour = (configuration?.extraData as VFATestCustomConfiguration | undefined)
    ?.testBehaviour;
  return (
    !!testType &&
    countsFixationLossesOnMovingFixation(testType) &&
    isSuprathresholdBehaviour(testBehaviour)
  );
}

export function TurnOffBlindSpotControlsOnEnteringMovingFixation(
  currentConfig: TestConfiguration | null | undefined,
  requestedUpdate: Partial<TestConfiguration>,
): Partial<TestConfiguration> {
  const currentExtraData = currentConfig?.extraData as VFATestCustomConfiguration | undefined;
  const requestedExtraData = requestedUpdate.extraData as VFATestCustomConfiguration | undefined;
  const nextConfig: Partial<TestConfiguration> = {
    testType: requestedUpdate.testType ?? currentConfig?.testType,
    extraData: { ...currentExtraData, ...requestedExtraData } as VFATestCustomConfiguration,
  };
  if (
    !isMovingFixationSuprathreshold(nextConfig) ||
    isMovingFixationSuprathreshold(currentConfig)
  ) {
    return requestedUpdate;
  }
  return {
    ...requestedUpdate,
    extraData: {
      ...(nextConfig.extraData as VFATestCustomConfiguration),
      blindSpotTesting: false,
      blindSpotMonitoringEnabled: false,
    },
  };
}

/**
 * @summary If Stimuli Size is greater than Goldmann III, Blind Spot Testing Should be disabled
 *  Similarly, if Blind Spot Testing is enabled, Stimuli Size should be set to Goldmann III
 * @param currentConfig - Current Test Configuration
 * @param requestedUpdate - Requested Update to Test Configuration
 * @returns Corrected Configurations for Blind Spot Testing and Stimuli Size, or null if fields are unchanged or non-existent
 */
export function GetCorrectedBlindSpotStimuliSizeConfigs(
  currentConfig: TestConfiguration,
  requestedUpdate: Partial<TestConfiguration>,
): Partial<TestConfiguration> {
  const currentStimuliSize = (currentConfig?.extraData as VFATestCustomConfiguration)?.stimuliSize;
  const currentBlindSpotTesting = (currentConfig?.extraData as VFATestCustomConfiguration)
    ?.blindSpotTesting;
  const requestedStimuliSize = (requestedUpdate?.extraData as VFATestCustomConfiguration)
    ?.stimuliSize;
  const requestedBlindSpotTesting = (requestedUpdate?.extraData as VFATestCustomConfiguration)
    ?.blindSpotTesting;

  const isStimuliSizeChanged =
    requestedStimuliSize !== undefined && requestedStimuliSize !== currentStimuliSize;
  const isStimuliSizeBig =
    requestedStimuliSize !== undefined && requestedStimuliSize !== GoldmannSize.GOLDMANN_III;

  const isBlindSpotTestingChanged =
    requestedBlindSpotTesting !== undefined &&
    requestedBlindSpotTesting !== currentBlindSpotTesting;
  const isBlindSpotTestingBeingEnabled = requestedBlindSpotTesting === true;

  if (
    (isStimuliSizeChanged && !isStimuliSizeBig) ||
    (isBlindSpotTestingChanged && !isBlindSpotTestingBeingEnabled) ||
    (!isStimuliSizeChanged && !isBlindSpotTestingChanged)
  ) {
    return requestedUpdate;
  }

  if (isStimuliSizeChanged && isStimuliSizeBig) {
    return {
      ...requestedUpdate,
      extraData: {
        ...(requestedUpdate?.extraData as VFATestCustomConfiguration),
        blindSpotTesting: false,
        stimuliSize: requestedStimuliSize,
      },
    };
  }

  if (isBlindSpotTestingChanged && isBlindSpotTestingBeingEnabled) {
    return {
      ...requestedUpdate,
      extraData: {
        ...(requestedUpdate?.extraData as VFATestCustomConfiguration),
        blindSpotTesting: true,
        stimuliSize: GoldmannSize.GOLDMANN_III,
      },
    };
  }

  return requestedUpdate;
}
